// src/app/modules/collaborateur/MesAvantages.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Gift,
    Heart,
    Bike,
    Home,
    Coffee,
    Baby,
    Plane,
    Settings,
    ChevronDown,
    ChevronUp,
    Info,
    Calendar,
    DollarSign,
    CheckCircle,
} from "lucide-react";
import "../MesrequetesStyles/MesAvantages.css";

// Action Redux à créer pour récupérer les avantages du collaborateur
import { fetchMesAvantagesRequest } from "../../../../redux/actions/mesAvantagesActions";

const MesAvantages = () => {
    const dispatch = useDispatch();

    // État Redux - adapter selon votre structure
    const { loading, avantages, categories } = useSelector(
        (state) => state.mesAvantages || {}
    );

    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [selectedAvantage, setSelectedAvantage] = useState(null);

    // Charger les avantages au montage
    useEffect(() => {
        dispatch(fetchMesAvantagesRequest());
    }, [dispatch]);

    // Grouper les avantages par catégorie
    const avantagesParCategorie = useMemo(() => {
        if (!avantages || !categories) return [];

        const grouped = categories.map((cat) => {
            const avantageCat = (avantages || []).filter(
                (av) => av.categorieId === cat.id && av.actif
            );
            return {
                ...cat,
                avantages: avantageCat,
                totalMontant: avantageCat.reduce((sum, av) => sum + (av.montant || 0), 0),
            };
        }).filter((cat) => cat.avantages.length > 0);

        return grouped;
    }, [avantages, categories]);

    // Calculer le montant total
    const montantTotal = useMemo(() => {
        return avantagesParCategorie.reduce((sum, cat) => sum + cat.totalMontant, 0);
    }, [avantagesParCategorie]);

    // Toggle expansion d'une catégorie
    const toggleCategory = (catId) => {
        setExpandedCategories((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(catId)) {
                newSet.delete(catId);
            } else {
                newSet.add(catId);
            }
            return newSet;
        });
    };

    // Mapper les icônes
    const getIconComponent = (name) => {
        const map = { Coffee, Heart, Bike, Home, Gift, Baby, Plane, Settings };
        return map[name] || Settings;
    };

    // Mapper les labels de fréquence
    const getFrequenceLabel = (freq) => {
        const map = {
            MENSUEL: "Par mois",
            ANNUEL: "Par an",
            JOUR: "Par jour",
            UNIQUE: "Unique",
        };
        return map[freq] || freq;
    };

    // Ouvrir le détail d'un avantage
    const openDetail = (avantage) => {
        setSelectedAvantage(avantage);
    };

    const closeDetail = () => {
        setSelectedAvantage(null);
    };

    return (
        <div className="maPage">
            {/* Header */}
            <div className="maHeader">
                <div>
                    <h1>Mes avantages sociaux</h1>
                    <p>Découvrez tous vos avantages et primes</p>
                    {loading && <div className="maMuted" style={{ marginTop: 6 }}>Chargement...</div>}
                </div>
            </div>

            {/* Résumé global */}
            <div className="maSummary">
                <div className="maSummaryCard">
                    <div className="maSummaryIcon">
                        <Gift size={24} />
                    </div>
                    <div className="maSummaryContent">
                        <div className="maSummaryLabel">Total des avantages</div>
                        <div className="maSummaryValue">{avantages?.length || 0}</div>
                    </div>
                </div>

                {/*<div className="maSummaryCard maSummaryCard--accent">*/}
                {/*    <div className="maSummaryIcon">*/}
                {/*        <DollarSign size={24} />*/}
                {/*    </div>*/}
                {/*    <div className="maSummaryContent">*/}
                {/*        <div className="maSummaryLabel">Montant total mensuel</div>*/}
                {/*        <div className="maSummaryValue">{montantTotal.toFixed(2)} DHS</div>*/}
                {/*    </div>*/}
                {/*</div>*/}

                <div className="maSummaryCard">
                    <div className="maSummaryIcon">
                        <CheckCircle size={24} />
                    </div>
                    <div className="maSummaryContent">
                        <div className="maSummaryLabel">Catégories actives</div>
                        <div className="maSummaryValue">{avantagesParCategorie.length}</div>
                    </div>
                </div>
            </div>

            {/* Liste des avantages par catégorie */}
            <div className="maContent">
                {avantagesParCategorie.length === 0 && !loading && (
                    <div className="maEmpty">
                        <Gift size={48} />
                        <h3>Aucun avantage disponible</h3>
                        <p>Vous n'avez pas encore d'avantages sociaux configurés.</p>
                    </div>
                )}

                {avantagesParCategorie.map((cat) => {
                    const Icon = getIconComponent(cat.icone);
                    const isExpanded = expandedCategories.has(cat.id);

                    return (
                        <div className="maCategory" key={cat.id}>
                            <div
                                className="maCategory__header"
                                onClick={() => toggleCategory(cat.id)}
                            >
                                <div className="maCategory__left">
                                    <div
                                        className="maCategory__icon"
                                        style={{ backgroundColor: cat.couleur }}
                                    >
                                        <Icon size={20} />
                                    </div>
                                    <div>
                                        <div className="maCategory__name">{cat.nom}</div>
                                        <div className="maMuted">
                                            {cat.avantages.length} avantage{cat.avantages.length > 1 ? "s" : ""}
                                        </div>
                                    </div>
                                </div>

                                <div className="maCategory__right">
                                    {cat.totalMontant > 0 && (
                                        <div className="maCategory__total">
                                            {cat.totalMontant.toFixed(2)} DHS
                                        </div>
                                    )}
                                    <button className="maIconBtn" type="button">
                                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </button>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="maCategory__content">
                                    <div className="maGrid">
                                        {cat.avantages.map((av) => (
                                            <div
                                                className="maCard"
                                                key={av.id}
                                                onClick={() => openDetail(av)}
                                            >
                                                <div className="maCard__header">
                                                    <div className="maCard__titleRow">
                                                        <span
                                                            className="maCode"
                                                            style={{ backgroundColor: cat.couleur }}
                                                        >
                                                            {av.code}
                                                        </span>
                                                        <span className="maCard__title">{av.nom}</span>
                                                    </div>
                                                    <button
                                                        className="maInfoBtn"
                                                        type="button"
                                                        title="Plus de détails"
                                                    >
                                                        <Info size={16} />
                                                    </button>
                                                </div>

                                                {av.description && (
                                                    <div className="maCard__desc">{av.description}</div>
                                                )}

                                                {/*<div className="maCard__details">*/}
                                                {/*    {av.montant !== null && av.montant !== undefined && (*/}
                                                {/*        <div className="maDetail">*/}
                                                {/*            <DollarSign size={14} />*/}
                                                {/*            <span className="maDetail__value">*/}
                                                {/*                {av.montant} DHS*/}
                                                {/*            </span>*/}
                                                {/*        </div>*/}
                                                {/*    )}*/}

                                                {/*    <div className="maDetail">*/}
                                                {/*        <Calendar size={14} />*/}
                                                {/*        <span>{getFrequenceLabel(av.frequence)}</span>*/}
                                                {/*    </div>*/}
                                                {/*</div>*/}

                                                {av.regle && (
                                                    <div className="maCard__footer">
                                                        <div className="maRegle">
                                                            <span className="maRegle__label">Règle:</span>
                                                            <span className="maRegle__value">{av.regle.nom}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Modal de détail */}
            {selectedAvantage && (
                <div className="maModal" onClick={closeDetail}>
                    <div
                        className="maModal__panel"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="maModal__header">
                            <div>
                                <div className="maModal__titleRow">
                                    <span
                                        className="maCode maCode--large"
                                        style={{
                                            backgroundColor:
                                                categories.find((c) => c.id === selectedAvantage.categorieId)?.couleur ||
                                                "#ee645f",
                                        }}
                                    >
                                        {selectedAvantage.code}
                                    </span>
                                    <h2 className="maModal__title">{selectedAvantage.nom}</h2>
                                </div>
                                {selectedAvantage.description && (
                                    <p className="maModal__subtitle">{selectedAvantage.description}</p>
                                )}
                            </div>
                            <button
                                className="maIconBtn"
                                onClick={closeDetail}
                                type="button"
                                aria-label="Fermer"
                            >
                                ×
                            </button>
                        </div>

                        <div className="maModal__body">
                            <div className="maDetailGrid">
                                {selectedAvantage.montant !== null && selectedAvantage.montant !== undefined && (
                                    <div className="maDetailBox">
                                        <div className="maDetailBox__label">Montant</div>
                                        <div className="maDetailBox__value maDetailBox__value--accent">
                                            {selectedAvantage.montant} DHS
                                        </div>
                                    </div>
                                )}

                                <div className="maDetailBox">
                                    <div className="maDetailBox__label">Fréquence</div>
                                    <div className="maDetailBox__value">
                                        {getFrequenceLabel(selectedAvantage.frequence)}
                                    </div>
                                </div>

                                {selectedAvantage.regle && (
                                    <>
                                        <div className="maDetailBox maDetailBox--wide">
                                            <div className="maDetailBox__label">Règle d'attribution</div>
                                            <div className="maDetailBox__value">{selectedAvantage.regle.nom}</div>
                                        </div>

                                        {selectedAvantage.regle.condition && (
                                            <div className="maDetailBox maDetailBox--wide">
                                                <div className="maDetailBox__label">Condition</div>
                                                <div className="maDetailBox__text">
                                                    {selectedAvantage.regle.condition}
                                                </div>
                                            </div>
                                        )}

                                        {selectedAvantage.regle.valeur && (
                                            <div className="maDetailBox maDetailBox--wide">
                                                <div className="maDetailBox__label">Valeur</div>
                                                <div className="maDetailBox__text">
                                                    {selectedAvantage.regle.valeur}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="maModal__footer">
                            <button className="maBtn maBtn--primary" onClick={closeDetail} type="button">
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MesAvantages;